
import { RevContainer } from '@ds/components/Container'
import isEmpty from 'lodash/isEmpty'
import { parse } from 'query-string'
import { validate as validateUUID } from 'uuid'
import { mapGetters } from 'vuex'

import ToastWrapper from '@core/components/ToastWrapper/'
import { parsedHash } from '@core/helpers'
import { productHomeDeepLinkBuilder } from '@core/helpers/deepLink'
import MyFavoritesToggle from '@dashboard/my-favorites/components/toggle'
import {
  fetchSEOTags,
  productBreadcrumbFetch,
  productDetailsV2Fetch,
  productEcoBlockFetch,
  productGradesFetchV3,
  productPickersFetch,
  productTechnicalSpecifications,
} from '@http/endpoints'
import {
  REVIEWS_TYPES,
  getGlobalRateRequest,
  getReviewsListRequest,
} from '@internal-reviews/helpers'
import logger from '@logger'
import { filterGradesABTestedPaymentMethods } from '@payment'
import SatisfactionModal from '@reassurance/components/SatisfactionModal'
import TrustList from '@reassurance/components/TrustList'
import RecommendationBundle from '@recommendation/components/RecommendationBundle/RecommendationBundle.vue'
import { logReviewsError } from '@reviews/helpers'
import { ROUTES } from '@router'
import { PRODUCT_DEAL_TYPE, getProductInfos } from '@tracking'
import { trackProductPage } from '@tracking/events'

import AboutUs from '../components/AboutUs'
import AddToCartModal from '../components/AddToCartModal'
import Breadcrumb from '../components/Breadcrumb'
import CustomizationFunnel from '../components/CustomizationFunnel'
import StepLayout from '../components/CustomizationFunnel/components/StepLayout'
import Details from '../components/Details'
import DynamicContainer from '../components/DynamicContainer'
import EcoBlock from '../components/EcoBlock'
import FixedContainer from '../components/FixedContainer'
import OutOfStock from '../components/OutOfStock'
import PopularSearchBlock from '../components/PopularSearchBlock'
import ProductBanner from '../components/ProductBanner'
import RecommendationsBlock from '../components/RecommendationsBlock'
import ReviewsBlock from '../components/ReviewsBlock'
import { NB_REVIEWS_DISPLAYED } from '../components/ReviewsBlock/ReviewsBlock.constants'
import SeoSellerContent from '../components/SeoSellerContent'
import SocialProof from '../components/SocialProof'
import {
  SWAP_DISABLED,
  SWAP_ERROR,
  SWAP_LOADING,
  SWAP_NONE,
  SWAP_SUCCESS,
} from '../components/SwapBlock'
import { head } from '../utils/head'
import { logDesync } from '../utils/logDesync'

import translations from './product.translations'

export default {
  name: 'Product',

  components: {
    DynamicContainer,
    CustomizationFunnel,
    MyFavoritesToggle,
    FixedContainer,
    AddToCartModal,
    Breadcrumb,
    RevContainer,
    StepLayout,
    EcoBlock,
    AboutUs,
    PopularSearchBlock,
    ProductBanner,
    RecommendationsBlock,
    RecommendationBundle,
    ReviewsBlock,
    SatisfactionModal,
    SeoSellerContent,
    SocialProof,
    ToastWrapper,
    TrustList,
    OutOfStock,
    Details,
  },
  // TODO https://backmarket.atlassian.net/browse/FRONT-429
  // convert the default Nuxt layout to the empty one.
  layout: 'empty',

  transition({ hash }, { name } = {}) {
    if (name === ROUTES.PRODUCT.HOME && hash.includes('scroll=false')) {
      return 'none'
    }

    return 'page'
  },

  async asyncData({ store, route, error, app, redirect }) {
    const { hash, params } = route
    const productId = params.uuid
    const productSlug = params.slugV2
    const hashParsed = parsedHash(hash.substring(1))
    const l = hashParsed.l?.[0] ? parseInt(hashParsed.l[0], 10) : undefined
    const offerType = hashParsed?.offerType ?? undefined

    // A unvalid uuid leads to 404 errors.
    // If a part of the url is encoded, the uuid gets corrupted.
    // ex: c84deba7-b0dd-4dc3-977e-61cc3530a540%23l=10
    // Here we fix the uuid and we redirect with a 301.
    if (!validateUUID(productId)) {
      const uuid = productId.match(
        /^[0-9a-fA-F]{8}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{12}/g,
      )

      // validateUUID is a bit stricter than our regex above… and than the validation in badoom. For instance
      // `29512ad0-ac02-7668-3c97-e2b45a4ae07a` is not valid using validateUUID but OK using our regex, due to
      // the first number of the third block. Let's avoid redirecting to the same page and breaking everything.
      if (uuid !== null && productId !== uuid[0]) {
        logger.info('Product unvalid uuid redirection')

        return redirect(301, {
          name: ROUTES.PRODUCT.HOME,
          params: {
            ...params,
            uuid: uuid[0],
          },
        })
      }
    }

    // Those calls are non blocking, meaning that the page MUST be rendered even if any of them is failing,
    // resulting in missing blocks in the page (but the user will still be able to see the product and add it to
    // the cart).
    // Note that those calls also don't handle the rematching feature (a product that isn't sold anymore but
    // redirected to another product), so all of them will result in a 404 if the product is rematched. We could avoid
    // this by calling the product_v2 endpoint before, but with the cost of increasing the page load.
    const nonBlockingRequests = [
      store
        .dispatch('http/request', {
          request: productBreadcrumbFetch,
          pathParams: { id: productId },
        })
        .catch(() => ({ payload: [] })),

      store
        .dispatch('http/request', {
          request: fetchSEOTags,
          queryParams: {
            model: 'product',
            pk: productId,
          },
        })
        .catch(() => ({ payload: [] })),

      store
        .dispatch('http/request', {
          request: productTechnicalSpecifications,
          pathParams: { id: productId },
        })
        .catch(() => ({ payload: {} })),

      store
        .dispatch('http/request', {
          request: productEcoBlockFetch,
          pathParams: { productId },
        })
        .catch(() => ({ payload: { display: false } })),

      store
        .dispatch('http/request', {
          request: productPickersFetch,
          pathParams: { id: productId },
          queryParams: {
            ...(l && { grade: l }),
            ...(offerType && { special_offer_type: offerType }),
          },
        })
        .catch(() => ({ payload: { pickers: [] } })),
    ]

    if (app.$config.KILL_REVIEW_PRODUCT_PAGE === true) {
      nonBlockingRequests.push(
        Promise.resolve({ payload: {} }),
        Promise.resolve({
          payload: [],
        }),
      )
    } else {
      nonBlockingRequests.push(
        store
          .dispatch(
            'http/request',
            getGlobalRateRequest({
              type: REVIEWS_TYPES.PRODUCT,
              params: { id: productId },
            }),
          )
          .catch((err) => {
            logReviewsError({ tags: ['ProductPage'], err })

            return { payload: {} }
          }),
        store
          .dispatch(
            'http/request',
            getReviewsListRequest({
              type: REVIEWS_TYPES.PRODUCT,
              params: { id: productId },
              route,
              limit: NB_REVIEWS_DISPLAYED,
            }),
          )
          .catch((err) => {
            logReviewsError({ tags: ['ProductPage'], err })

            return { payload: {} }
          }),
      )
    }

    try {
      /**
       * Temporarily override the default query params in the product endpoint for the original price A/B test
       * @see https://github.com/BackMarket/pastrami/blob/main/modules/http/endpoints.js#L221-L225
       * @see https://backmarket.atlassian.net/browse/BRO-3085
       */
      const include =
        store.getters['flags/getExperiments']
          ?.ppHidePriceWhenNewOnOldDevices === 'hideOnOldDevices'
          ? 'checks,obsolescence,waterproof,accessories'
          : 'checks,obsolescence,waterproof,accessories,originalPrice'

      const blockingRequests = [
        store.dispatch('http/request', {
          request: productGradesFetchV3,
          pathParams: { id: productId },
          queryParams: {
            offer_type: offerType,
          },
        }),

        store.dispatch('http/request', {
          request: productDetailsV2Fetch,
          pathParams: { id: productId },
          queryParams: { include },
        }),
      ]

      const [
        { payload: grades },
        { payload: product },
        { payload: breads },
        { payload: tags },
        { payload: technicalSpecifications },
        { payload: ecoBlock },
        { payload: pickers },
        { payload: ratings },
        { payload: reviews },
      ] = await Promise.all([...blockingRequests, ...nonBlockingRequests])

      const fetchedId = product.link.params.uuid.toString()
      const fetchedSlug = product.link.params.slugV2

      // If ProductV2 endpoint has rematched the requested product to a different product,
      // we redirect to this rematched product instead.
      // /!\ Only product_v2 and best_offers_V3 endpoints handles the rematch feature, as it's quite
      // heavy on the database. All the other calls will results in a 404.
      if (productSlug !== fetchedSlug || productId !== fetchedId) {
        logger.info('Product rematch redirection')

        return redirect(301, {
          name: ROUTES.PRODUCT.HOME,
          params: {
            locale: params.locale,
            uuid: fetchedId,
            slugV2: fetchedSlug,
          },
        })
      }

      const selectedByDefault = grades.find(
        (grade) => grade.isSelectedByDefault,
      )

      const doesGradeExist = grades.some(
        (grade) => grade.backboxGrade.value === l && !grade.isDisabled,
      )

      const activeBackboxGrade = doesGradeExist
        ? l
        : selectedByDefault && selectedByDefault.backboxGrade.value

      return {
        activeBackboxGrade,
        breads,
        grades: filterGradesABTestedPaymentMethods(
          store.getters['flags/getExperiments'],
          grades,
        ),
        product,
        productId,
        productSlug: fetchedSlug,
        ratings,
        reviews: reviews?.results || [],
        shouldUpdateGradeInURL: !doesGradeExist,
        tags,
        technicalSpecifications,
        pickers: pickers.pickers,
        ecoBlock,
      }
    } catch (err) {
      return error({
        statusCode: err.status_code,
        message: app.$t(translations.productPageNotFoundError),
      })
    }
  },

  data() {
    return {
      activeBackboxGrade: null,
      breads: [],
      grades: [],
      hasSwapFailed: false,
      isSwapLoading: true,
      product: {},
      productId: '',
      ratings: {},
      redirectWithNewGrade: false,
      reviews: [],
      shouldUpdateGradeInURL: false,
      tags: [],
      technicalSpecifications: {},
      pickers: [],
      productAlternative: {},
      additionalListings: [],
      ecoBlock: { display: false },
    }
  },

  head() {
    return head({
      $store: this.$store,
      $route: this.$route,
      $navigation: this.$navigation,
      product: this.product,
      ratings: this.ratings,
      price: !isEmpty(this.activeGrade) ? this.activeGrade.price : {},
      isOutOfStock: this.isOutOfStock,
    })
  },

  computed: {
    listingId() {
      return this.offers.activeGrade.id
    },

    offerType() {
      return parseInt(parse(this.$route.hash).offerType || '0', 10)
    },

    bundleTitle() {
      return this.$t(translations.bundleTitle)
    },

    ...mapGetters({
      experiments: 'flags/getExperiments',
      isSwapEnabled: 'swap/isSwapEnabled',
      swapEstimations: 'swap/estimations',
    }),

    translations: () => translations,

    activeGrade() {
      return (
        this.grades.find(
          (grade) => grade.backboxGrade.value === this.activeBackboxGrade,
        ) || {}
      )
    },

    offers() {
      return {
        activeBackboxGrade: this.activeBackboxGrade,
        activeGrade: this.activeGrade,
        grades: this.grades,
      }
    },

    productContainerClasses() {
      return [
        'bg-white text-black max-w-full mb-7',
        { 'pt-4 md:pt-6': !this.breads.length },
      ]
    },

    hasTags() {
      return !isEmpty(this.tags)
    },

    hasSellerContent() {
      return !isEmpty(this.product.description)
    },

    shouldDisplayEcoBlock() {
      return this.ecoBlock.display
    },

    shouldDisplayCustomizationFunnel() {
      return (
        this.experiments.ppMixAndMatch === 'withMixAndMatch' &&
        this.product.trackingDetails.category3MarketplaceCategory_ID === 2
      )
    },

    productFromInfos() {
      return getProductInfos(this.product.legacyId)
    },

    stock() {
      return this.activeGrade?.reassurance?.stock?.stockRawValue || 0
    },

    productTrackingInfo() {
      let color = null
      let dealType = PRODUCT_DEAL_TYPE.NORMAL

      if (!isEmpty(this.pickers)) {
        color =
          this.pickers
            .find((group) => group.id === 'color')
            ?.items.find((picker) => picker.selected)?.label || null

        dealType =
          this.pickers
            .find((group) => group.id === 'recommended_variants')
            ?.items.find((picker) => picker.selected)?.trackingValue ??
          PRODUCT_DEAL_TYPE.NORMAL
      }

      const merchantId =
        !isEmpty(this.activeGrade) && this.activeGrade.merchant
          ? String(this.activeGrade.merchant.id)
          : ''

      const image = this.product.images?.[0] ?? {}
      const webUrl = this.product.link?.href
      const deepLink = productHomeDeepLinkBuilder({ $route: this.$route })
      const mobileDeeplink = `backmarket://${deepLink}`
      const specialOfferType = parse(this.$route.hash).offerType || '0'

      return {
        averageRate: this.ratings.averageRate,
        brand: this.product.trackingDetails.brand,
        category: this.product.trackingDetails.category3Name,
        color,
        currency: !isEmpty(this.activeGrade)
          ? this.activeGrade.price.currency
          : null,
        dealType,
        hasReviews: this.areReviewsDisplayed,
        id: Number(this.product.legacyId),
        image: image.url,
        isSwapEnabled: this.isSwapEligible,
        list: this.productFromInfos.source,
        listingId: !isEmpty(this.activeGrade)
          ? String(this.activeGrade.id)
          : null,
        merchantId,
        mobileDeeplink,
        model: this.product.model,
        name: this.product.rawTitle,
        numberReviewsDisplayed: this.reviewsList.length,
        numberTotalReviews: this.ratings.reviewsCount,
        price: !isEmpty(this.activeGrade)
          ? this.activeGrade.price.amount
          : null,
        stock: this.stock,
        uuid: this.product.id,
        variant: !isEmpty(this.activeGrade)
          ? this.activeGrade.backboxGrade.value
          : null,
        webUrl,
        shippingDelay:
          this.activeGrade.reassurance?.shipping?.free?.delayInHours ?? 0,
        hasExpressShipping: !!this.activeGrade.reassurance?.shipping?.express,
        hasNewBattery: ['disabled', 'available'].includes(
          this.activeGrade?.newBattery?.status,
        ),
        hasNewBatteryInStock:
          this.activeGrade?.newBattery?.status === 'available',
        specialOfferType,
      }
    },

    listingsTrackingInfos() {
      return this.grades.map(({ id, backboxGrade = {}, price = {} }) => ({
        id,
        variant: backboxGrade.value,
        price: price.amount,
      }))
    },

    swapOffer() {
      return this.swapEstimations.find(
        (estimation) => estimation.id === this.offers.activeGrade.id,
      )
    },

    isSwapInCart() {
      return this.swapOffer?.isOffer
    },

    isSwapEligible() {
      return this.product.isSwapEligible
    },

    noSwapHaveBeenEstimated() {
      return (
        !isEmpty(this.swapOffer) && this.isSwapEnabled && !this.isSwapInCart
      )
    },

    swapStatus() {
      if (!this.isSwapEligible) {
        return SWAP_DISABLED
      }

      if (this.isSwapLoading) {
        return SWAP_LOADING
      }

      if (this.hasSwapFailed) {
        return SWAP_ERROR
      }

      if (this.noSwapHaveBeenEstimated) {
        return SWAP_NONE
      }

      return SWAP_SUCCESS
    },

    areReviewsDisplayed() {
      return this.ratings.reviewsCount > 0
    },

    landingReviewsLink() {
      if (this.ratings.reviewsCount < 1) {
        return {}
      }

      const route = {
        name: ROUTES.REVIEWS.PAGES.PRODUCT,
        params: {
          productId: this.productId,
        },
        query: {
          productName: this.product.title,
        },
      }

      return route
    },
    reviewsList() {
      return this.reviews.slice(0, NB_REVIEWS_DISPLAYED)
    },
    isOutOfStock() {
      const { grades } = this.offers

      return grades.every((grade) => grade.isDisabled)
    },
  },

  watch: {
    async productId() {
      await this.fetchListingData()
      await this.refreshSwap()
    },

    '$route.hash': async function onHashChange(newHash, prevHash) {
      await this.fetchListingData()
      await this.refreshPickers()

      const prevOfferType = parse(prevHash).offerType ?? undefined
      const newOfferType = parse(newHash).offerType ?? undefined

      if (newOfferType !== prevOfferType) {
        await this.refreshBestOffers()
      }

      await this.refreshSwap()
    },

    '$route.params': function onParamsChange(currentParams, previousParams) {
      if (currentParams.id !== previousParams.id) {
        this.$nuxt.refresh()
      }
    },

    '$route.query': {
      async handler() {
        try {
          const { payload: reviews } = await this.$store.dispatch(
            'http/request',
            getReviewsListRequest({
              type: REVIEWS_TYPES.PRODUCT,
              params: { id: this.$route.params.uuid },
              route: this.$route,
              limit: NB_REVIEWS_DISPLAYED,
            }),
          )
          this.reviews = reviews?.results || []
        } catch (err) {
          logReviewsError({ tags: ['ProductPage', 'Filter'], err })
        }
      },
    },
  },

  updated() {
    const hash = parsedHash(this.$route.hash)

    if (
      this.shouldUpdateGradeInURL &&
      hash.l?.[0] &&
      parseInt(hash.l?.[0], 10) !== this.activeBackboxGrade
    ) {
      this.$navigation.replace({
        type: 'internal',
        name: this.$route.name,
        params: this.$route.params,
        query: this.$route.query,
        hash: {
          ...hash,
          l: this.activeBackboxGrade,
        },
      })
    }
    this.shouldUpdateGradeInURL = false
  },

  async mounted() {
    await this.fetchListingData()

    const { offerType } = parse(this.$route.hash)

    // TODO [BRO-3238] stop using hash params
    // Force refresh on load as hash is only available client-side
    if (offerType && Number(offerType) > 0) {
      await this.refreshPickers()
      await this.refreshBestOffers()
    }

    await this.refreshSwap()
  },

  methods: {
    async refreshBestOffers() {
      const { offerType } = parse(this.$route.hash)

      try {
        const { payload } = await this.$store.dispatch('http/request', {
          request: productGradesFetchV3,
          pathParams: { id: this.productId },
          queryParams: {
            ...(offerType && { offer_type: offerType }),
          },
        })

        this.grades = payload
      } catch (error) {
        logger.error('[PRODUCT] Error refreshing best offers', { error })
      }
    },

    async refreshPickers() {
      const { offerType } = parse(this.$route.hash)

      try {
        const { payload } = await this.$store.dispatch('http/request', {
          request: productPickersFetch,
          pathParams: { id: this.productId },
          queryParams: {
            grade: this.activeBackboxGrade,
            ...(offerType && { special_offer_type: offerType }),
          },
        })

        this.pickers = payload.pickers
      } catch (error) {
        logger.error('[PRODUCT] Error refreshing pickers', { error })
      }
    },

    async refreshSwap() {
      if (this.isSwapEligible) {
        try {
          await this.$store.dispatch(
            'swap/getSwapEstimation',
            this.activeGrade.id,
          )
          this.hasSwapFailed = false
        } catch {
          this.hasSwapFailed = true
        }
      }

      this.isSwapLoading = false
    },

    onAddToCartModalClose() {
      this.additionalListings = []
    },
    onRecommendationBundleAddToCart({ additionalListings }) {
      this.additionalListings = additionalListings
    },
    onRecommendationsLoaded(results) {
      this.productAlternative = results?.length
        ? { product: results[0] }
        : {
            landing: this.breads.slice(-2, -1)[0],
          }
    },
    async fetchListingData() {
      if (parse(this.$route.hash).l) {
        this.setActiveBackboxGrade(parse(this.$route.hash).l)
      }

      logDesync({
        $store: this.$store,
        $route: this.$route,
        legacyId: this.product.legacyId,
        isOutOfStock: this.isOutOfStock,
        price: this.activeGrade?.price?.amount,
        ratings: this.ratings,
      })
      trackProductPage({
        product: this.productTrackingInfo,
        listings: this.listingsTrackingInfos,
      })
    },
    setActiveBackboxGrade(newBackboxGrade) {
      const backboxGradeInteger = parseInt(newBackboxGrade, 10)

      const doesGradeExist = this.grades.some(
        (grade) =>
          grade.backboxGrade.value === backboxGradeInteger && !grade.isDisabled,
      )

      if (doesGradeExist && backboxGradeInteger !== this.activeBackboxGrade) {
        this.activeBackboxGrade = backboxGradeInteger
      }
    },
  },
}
